import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Move, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { Search, Users, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { usePersons } from "@/hooks/usePersons";
import { findRoots } from "@/lib/tree";
import { TreeView } from "@/components/FamilyBranch";
import { ZoomCanvas } from "@/components/ZoomCanvas";
import { Avatar } from "@/components/PersonCard";
import { StoryFeed } from "@/components/StoryFeed";
import { PersonDialog } from "@/components/PersonDialog";
import { FamilyWealth } from "@/components/FamilyWealth";
import { BranchWealth } from "@/components/BranchWealth";
import { LineageLeaderboard } from "@/components/LineageLeaderboard";
import { t as tr, getLang } from "@/lib/i18n";

const HERO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_kin-branches/artifacts/b723f7ab37fafe44_WhatsApp%20Image%202026-10-15%20at%2012.46.02%20AM.jpeg";

const Hero = ({ count }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 pb-12 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
    <div className="rise">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-5">{tr("Life long Jurny")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1C2421] leading-[1.05]">
        {tr("Every branch of the")} <em className="text-[#1E3A2B]">{getLang() === "hi" ? "वत्स" : "Vats"}</em> {tr("family, in one living tree.")}
      </h1>
      <p className="mt-6 text-base md:text-lg text-[#52635B] max-w-xl leading-relaxed">
        {tr("Tap any name to trace their ancestors above and descendants below. Family members can join and take their place — once three relatives vouch for them.")}
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Button asChild className="rounded-full bg-[#D96B43] hover:bg-[#C05731] px-7 h-12" data-testid="hero-join-btn">
          <Link to="/join">{tr("Take your place")}</Link>
        </Button>
        <span className="flex items-center gap-2 text-sm text-[#52635B]" data-testid="hero-member-count">
          <Users className="w-4 h-4 text-[#1E3A2B]" /> {count} {tr("members on the tree")}
        </span>
      </div>
    </div>
    <div className="relative rise" style={{ animationDelay: ".15s" }}>
      <div className="w-full h-[340px] overflow-hidden rounded-[2rem]">
        <img src={HERO} alt="Vats family portrait" className="w-full h-full object-cover object-center scale-[1.2]" data-testid="hero-family-photo" />
      </div>
      <div className="absolute -bottom-6 -left-4 bg-white border border-[#E5DEC9] rounded-2xl px-5 py-4 flex items-center gap-3 shadow-[0_12px_32px_-16px_rgba(30,58,43,.4)]">
        <ShieldCheck className="w-6 h-6 text-[#1E3A2B]" />
        <div className="text-sm"><b className="font-semibold">{tr("Verified by family")}</b><br /><span className="text-[#7E8E85]">{tr("3 approvals only")}</span></div>
      </div>
    </div>
  </section>
);

const SearchBox = ({ persons, onPick }) => {
  const [q, setQ] = useState("");
  const hits = q ? persons.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6) : [];
  return (
    <div className="relative w-full sm:w-80">
      <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#7E8E85]" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Find a family member…")} className="pl-10 h-11 rounded-full bg-white" data-testid="tree-search-input" />
      {hits.length > 0 && (
        <div className="absolute z-20 mt-2 w-full bg-white border border-[#E5DEC9] rounded-2xl p-1.5 shadow-lg">
          {hits.map((p) => (
            <button key={p.person_id} onClick={() => onPick(p)} data-testid={`search-result-${p.person_id}`} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[#F4EDE2] text-left">
              <Avatar p={p} size="w-8 h-8 text-xs" /> <span className="text-sm">{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Home() {
  const { persons, t, reload } = usePersons();
  const { user } = useAuth();
  const [arrange, setArrange] = useState(false);
  const myId = user?.person?.status === "approved" ? user.person.person_id : undefined;
  const reorder = async (ids) => {
    try {
      if (user?.is_admin) { await api.post("/admin/reorder", { ids }); toast.success("Order saved"); reload(); return; }
      await api.post("/order-requests", { ids });
      toast.success("New order sent — needs 5 family approvals");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const roots = useMemo(() => (persons ? findRoots(persons, t) : []), [persons, t]);
  const [sel, setSel] = useState(null);
  const open = (p) => setSel(p);

  return (
    <div>
      <Hero count={persons?.length ?? 0} />
      <FamilyWealth />
      <BranchWealth persons={persons} tree={t} onSelect={open} />
      <LineageLeaderboard persons={persons} tree={t} />
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-2">{tr("The family tree")}</p>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold">{tr("Click any name to see their lineage")}</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {user?.can_vote && persons?.length > 0 && (
              <Button variant={arrange ? "default" : "outline"} onClick={() => setArrange(!arrange)} data-testid="arrange-toggle-btn"
                className={`rounded-full h-11 ${arrange ? "bg-[#D96B43] hover:bg-[#C05731]" : ""}`}>
                <Move className="w-4 h-4 mr-2" />{arrange ? tr("Done arranging") : tr("Arrange elder / younger")}
              </Button>
            )}
            {persons?.length > 0 && (
              <Button asChild variant="outline" className="rounded-full h-11" data-testid="print-tree-link">
                <Link to="/print"><Printer className="w-4 h-4 mr-2" />{tr("Print tree")}</Link>
              </Button>
            )}
            {persons && <SearchBox persons={persons} onPick={open} />}
          </div>
        </div>
        {persons === null ? (
          <div className="h-64 rounded-3xl bg-[#F4EDE2] animate-pulse" />
        ) : persons.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#CDBF9F] p-16 text-center" data-testid="tree-empty-state">
            <p className="font-serif text-2xl">{tr("The tree is waiting for its first root.")}</p>
            <p className="text-[#7E8E85] mt-2">The first person to sign in becomes the admin and can add the eldest ancestor.</p>
          </div>
        ) : (
          <ZoomCanvas focusId={myId}>
            {arrange && <p className="text-center text-sm text-[#D96B43] mb-4" data-testid="arrange-hint">{tr("Drag a brother or sister onto another sibling to change elder → younger order (left = eldest).")}{!user?.is_admin && tr(" Your change is sent for approval: 5 family members.")}</p>}
            <TreeView roots={roots} t={t} onSelect={arrange ? undefined : open} activeId={myId} arrange={arrange} onReorder={reorder} />
          </ZoomCanvas>
        )}
      </section>
      <StoryFeed />
      {sel && <PersonDialog p={t.byId[sel.person_id] || sel} t={t} onClose={() => setSel(null)} onSelect={setSel} />}
    </div>
  );
}
